import { Content, List, Root } from '@radix-ui/react-tabs';
import { t } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import CustomTrigger from '~/ui/CustomTrigger/CustomTrigger';
import LeisureGeneral from '~/components/LeisureGeneral/LeisureGeneral';
import LeisureTournament from '~/components/LeisureTournament/LeisureTournament';
import { useFormik } from 'formik';
import { Button, Spinner } from '~/ui';
import { validate } from '~/utils/validateGameGeneral';
import styles from './Leisure.module.scss';
import { instance } from '~/utils/api/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import { IAsset } from '../Assets/Assets';

export interface IFormikValues {
  name: string;
  assets: { id: number; name: string }[];
  organizations: { id: number; name: string }[];
  accommodations: { id: number; name: string }[];
  description: string;
  image: {
    id: number;
    name: string;
    path: string;
    full_photo_url: string;
    status: string;
  };
  uploadImage: File | string | null;
}

const ACTIVE_TABS = {
  GENERAL: 'general',
  TOURNAMENTS: 'tournaments',
};

interface IGame {
  name: string;
  description: string;
  accommodations: { accommodation: { id: number; name: string } }[];
  organizations: { organizations: { id: number; name: string } }[];
  assets: { asset: { id: number; name: string } }[];
  full_logo_path: string;
}

const Leisure = () => {
  const [activeTab, setActiveTab] = useState(ACTIVE_TABS.GENERAL);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const searchTab = searchParams.get('activeTab');
  const navigate = useNavigate();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);

  const { data: game, isLoading } = useQuery<IGame>({
    queryFn: async () => {
      const { data } = await instance.get(`admin/game-info/${id}`);
      return data;
    },
    queryKey: ['game', id],
    onError: error => {
      toastError(error);
    },
  });

  const { data: assetsArray } = useQuery<IAsset[]>({
    queryFn: async () => {
      const { data } = await instance.get('admin/assets');
      return data.data;
    },
    queryKey: ['assets'],
    onError: error => {
      toastError(error);
    },
  });

  const {
    values,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    handleSubmit,
    isValid,
    setValues,
  } = useFormik<IFormikValues>({
    initialValues: {
      name: '',
      assets: [],
      organizations: [],
      accommodations: [],
      description: '',
      image: {
        id: 0,
        name: '',
        full_photo_url: '',
        status: '',
        path: '',
      },
      uploadImage: null,
    },
    validate,
    onSubmit: () => {
      updateGame();
    },
  });

  const accomodationParams = {
    sort: '',
    perPage: 100,
    field: '',
    page: 1,
    asset_ids: values.assets.map(item => item.id),
  };

  const { data: accomodationsArr, isLoading: isAccomodationsLoading } =
    useQuery({
      queryFn: async () => {
        const { data } = await instance.get(`admin/accommodations`, {
          params: {
            ...accomodationParams,
          },
        });
        return data;
      },
      queryKey: ['accommodations', accomodationParams],
      enabled: !!values.assets.length,
      onError: error => {
        toastError(error);
      },
    });

  const { mutateAsync: updateGame, isLoading: isUpdateGameLoading } =
    useMutation(
      async () => {
        const formData = new FormData();

        formData.append('name', values.name);
        formData.append('description', values.description);

        values.assets.forEach(item => {
          formData.append('asset_ids[]', String(item.id));
        });

        values.accommodations.forEach(item => {
          formData.append('accommodation_ids[]', String(item.id));
        });

        values.organizations.forEach(item => {
          formData.append('organization_ids[]', String(item.id));
        });

        if (values.uploadImage && values.uploadImage instanceof File) {
          formData.append('logo', values.uploadImage);
        }
        const response = await instance.post(
          `admin/game-info/${id}?_method=put`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        return response.data;
      },
      {
        onSuccess: () => {
          toastSuccess(t('game_successful_updated'));
          client.invalidateQueries(['game', id]);
          navigate(`/entertainment`);
        },
        onError: error => {
          toastError(error);
        },
      }
    );

  const resetImage = () => {
    setFieldValue('image', {
      id: 0,
      name: '',
      full_photo_url: '',
      status: '',
      path: '',
    });
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/entertainment/${id}?activeTab=${value}`);
  };

  useEffect(() => {
    if (searchTab) {
      setActiveTab(searchTab);
    }
  }, [searchTab]);

  useEffect(() => {
    if (game && !isLoading) {
      const accommodations = game?.accommodations?.map(item => {
        return {
          id: item.accommodation.id,
          name: item.accommodation.name,
        };
      });

      const organizations = game?.organizations?.map(item => {
        return {
          id: item.organizations.id,
          name: item.organizations.name,
        };
      });
      const assets = game?.assets?.map(item => {
        return {
          id: item.asset.id,
          name: item.asset.name,
        };
      });

      setValues({
        name: game.name,
        assets: isSuperAdmin ? assets : assetsArray || [],
        organizations,
        accommodations,
        description: game.description,
        image: {
          id: 1,
          name: game.name,
          path: '',
          full_photo_url: game.full_logo_path,
          status: '',
        },
        uploadImage: game.full_logo_path,
      });
    }
  }, [game]);

  useEffect(() => {
    if (!values.assets.length) return;
    if (!accomodationsArr || isAccomodationsLoading) return;

    const newAccommodations = values.accommodations.filter(item =>
      accomodationsArr.data.some((el: { id: number }) => el.id === item.id)
    );
    if (values.assets.length === 0) {
      setFieldValue('accommodations', []);
    } else {
      setFieldValue('accommodations', newAccommodations);
    }
  }, [values.assets, accomodationsArr]);

  return (
    <Page heading={<Heading text={game?.name} />}>
      <Root value={activeTab} onValueChange={value => handleTabChange(value)}>
        <List className={styles.rootsList}>
          <CustomTrigger
            key={1}
            value={ACTIVE_TABS.GENERAL}
            title={t('general')}
            activeTab={activeTab}
          />
          <CustomTrigger
            key={2}
            value={ACTIVE_TABS.TOURNAMENTS}
            title={t('tournaments')}
            activeTab={activeTab}
          />
        </List>
        <form onSubmit={handleSubmit}>
          <Content value={ACTIVE_TABS.GENERAL}>
            {game && !isLoading && !isUpdateGameLoading ? (
              <LeisureGeneral
                values={values}
                setFieldTouched={setFieldTouched}
                setFieldValue={setFieldValue}
                resetImage={resetImage}
                errors={errors}
                touched={touched}
                accomodationsArr={accomodationsArr?.data}
                // changeAssets={changeAssets}
              />
            ) : (
              <Spinner />
            )}
          </Content>
          <Content value={ACTIVE_TABS.TOURNAMENTS}>
            <LeisureTournament gameId={Number(id)} />
          </Content>
          <Button
            type="submit"
            className={styles.button}
            text={t('save_n_finish')}
            disabled={!isValid}
          />
        </form>
      </Root>
    </Page>
  );
};

export default Leisure;
